<script setup lang="ts">
/**
 * A user must always be authenticated to leave a review. If a user tries to access the review form
 * without being authenticated, they will be redirected to the account service for authentication.
 * Once successfully authenticated, the user will be redirected back to their original page,
 * and the review form will automatically open using a flag stored in session storage.
 */

import { checkShouldTriggerReviewForm, setShouldTriggerReviewForm } from '~/utils/trigger-review-form'
import type { AppReviewsResponse } from '~/server/api/apps/[app_id]/reviews'
import type { AppDetailsSchema } from '~/types/product.model'

const props = defineProps<{
  tabId: string
  data: AppDetailsSchema
}>()

const shouldShowReviewForm = ref(false)
const isLoggedIn = useLoggedInState()

const handleWriteReview = async (): Promise<void> => {
  if (isLoggedIn.value) {
    shouldShowReviewForm.value = true
    return setShouldTriggerReviewForm(false)
  }

  setShouldTriggerReviewForm(true)
  await login()
}

const closeModal = (): void => {
  shouldShowReviewForm.value = false
}

const { data: reviewsData } = await useFetch<AppReviewsResponse>(
  `/api/apps/${props.data.id}/reviews`,
)

onMounted(() => {
  openReviewFormAfterAuthenticating()
})

// Close review form if user logged out (e.g. via another browser tab)
watch(isLoggedIn, (isLoggedInState) => {
  if (!isLoggedInState) {
    shouldShowReviewForm.value = false
  }
})

/**
 * Automatically open up the review form after user got redirected back from a successful authentication.
 * If authentication failed (e.g. due to cancellation), clean up the session storage.
 */
function openReviewFormAfterAuthenticating(): void {
  if (!checkShouldTriggerReviewForm()) {
    return
  }

  if (!isLoggedIn.value) {
    return setShouldTriggerReviewForm(false)
  }

  handleWriteReview()
}
</script>

<template>
  <section class="app-reviews">
    <h2
      :id="tabId"
      class="heading-5"
    >
      Reviews
    </h2>

    <div class="app-reviews__container">
      <div class="app-reviews__sidebar">
        <ProductReviewRatingMinimal
          :rating-stats="data.rating_stats"
          :is-single-star="false"
          size="medium"
          show-when-no-ratings
          show-no-ratings-message
        />
        <ProductReviewRatingsBreakdown
          v-if="data.rating_stats"
          :ratings-breakdown="data.rating_stats.ratings_breakdown"
        />
        <UiButton
          type="secondary"
          @click="handleWriteReview"
        >
          Write a review
        </UiButton>
      </div>

      <ClientOnly>
        <Teleport to="body">
          <UiModal
            title="review-form"
            class="app-reviews__modal"
            data-test="review-form"
            :is-open="shouldShowReviewForm"
            :has-close="true"
            should-close-on-esc
            @close="closeModal"
          >
            <template #title>
              <h2 class="review-form__heading">
                {{ data.title }}
              </h2>
            </template>
            <ProductReviewFormEmbedded
              v-if="shouldShowReviewForm"
              :app-title="data.title"
              :app-id="data.id"
              @cancel="closeModal"
              @close="closeModal"
            />
          </UiModal>
        </Teleport>
      </ClientOnly>
      <div
        v-if="reviewsData?.length"
        class="app-reviews__list"
      >
        <ProductReview
          v-for="(review, index) in reviewsData"
          :key="index"
          :review="review"
        />
      </div>
      <p
        v-else
        class="app-reviews__no-reviews-message"
      >
        There are no reviews yet for this app. Be the first one to write a review.
      </p>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.app-reviews__container {
  display: flex;
  gap: var(--app-spacer-30);
  flex-direction: column;

  @include min-width('md') {
    flex-direction: row;
  }
}

.app-reviews__sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--app-spacer-20);

  @include min-width('md') {
    flex: 0 0 223px;
  }
}

.app-reviews__no-reviews-message {
  margin-top: var(--app-spacer-4);
  flex-basis: 100%;

  @include min-width('md') {
    text-align: center;
  }
}

.app-reviews__modal :deep(.app-modal__dialog) {
  max-width: 508px;
}

.app-reviews__list {
  flex: 1 1 auto;
}
</style>
